import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';

import RecordBadge, { RecordTitle } from '../common/RecordBadge';
import GroupHeader from '../common/GroupHeader';
import DataTypeBadge from '../common/DataTypeBadge';
import { formatCount, joinMeta } from '../../utils/counts';
import { groupElementsIntoPanels } from '../../utils/elements';
import { getMeasure } from '../../utils/units';
import { defaultUnitFor, measuresLabel, unitSymbol } from '../../utils/measurements';
import { normalizeId } from '../../utils/ids';
import { cardPagePath } from '../../utils/cards';
import { CARD_TAB } from '../cards/cardTabs';
import { choiceListOf } from '../../utils/choiceValues';

/**
 * THE ELEMENT SET HOME — what `/element-sets/:id` shows with no `?tab=`
 * (docs/platform-standards.md § The element set page): the card home's
 * shape for a set. Read-only and typographic — the set's name and
 * description, one meta line (scope · element count · the cards that
 * carry it), its elements as light rows with their type chip and the
 * STORED, namespaced name muted beside the label, and the cards it is
 * used by as badges. Every `GroupHeader` on an editable section carries a
 * quiet "Edit" door to its tab. It always renders the SAVED set.
 */

const MetaLine = ({ parts, className = '' }) => {
	const kept = parts.filter(Boolean);
	if (!kept.length) return null;
	return (
		<div className={['card-home__meta', 'meta-line', className].filter(Boolean).join(' ')}>
			{kept.map((part, index) => (
				<Fragment key={part.key || index}>
					{index > 0 ? <span className="card-home__dot" aria-hidden="true"> · </span> : null}
					{part}
				</Fragment>
			))}
		</div>
	);
};

const elementFacts = (element) => {
	const facts = [];
	if (element?.options?.required) facts.push('required');
	const choices = choiceListOf(element).length;
	if (choices) facts.push(`${choices} choice${choices === 1 ? '' : 's'}`);
	const measure = element?.dataType === 'measurement' ? getMeasure(element?.options?.measure) : null;
	// Several measures (KPR-1085): "Volume, mass or count · cup".
	if (measure) facts.push(`${measuresLabel(element.options) || measure.label} · ${unitSymbol(defaultUnitFor(element.options))}`);
	// A money element's currencies ("USD · CAD"), the editor row's chip.
	const currencies = element?.dataType === 'currency' && Array.isArray(element?.options?.currencies) ? element.options.currencies : [];
	if (currencies.length) facts.push(currencies.slice(0, 3).join(' · ') + (currencies.length > 3 ? ` +${currencies.length - 3}` : ''));
	return joinMeta(...facts);
};

export default function ElementSetHome({
	set,
	// The `include=usage` annotation: the readable cards that attach the set.
	usage = null,
	scopeTag = null,
	editable = false,
}) {
	const visual = { icon: set?.icon, color: set?.color, name: set?.name };
	const name = (set?.name || '').trim() || 'Untitled element set';
	const description = (set?.description || '').trim();
	const elements = useMemo(() => (Array.isArray(set?.elements) ? set.elements.filter(el => el?.name) : []), [set]);
	const panels = useMemo(() => groupElementsIntoPanels(elements), [elements]);
	const cards = Array.isArray(usage?.cards) ? usage.cards : [];

	const editLink = (tab, label) => (editable ? (
		<Link className="card-home__edit" to={`?tab=${tab}`} replace aria-label={label}>Edit</Link>
	) : null);

	return (
		<div className="card-home element-set-home">
			<div className="card-home__head">
				<RecordTitle kind="elementSet" record={visual} title={name} size="lg" wrap className="card-home__title" />
				{description ? <p className="card-home__description">{description}</p> : null}
				<MetaLine parts={[
					scopeTag ? <span key="scope" className="card-home__scope">{scopeTag}</span> : null,
					set?.key ? <span key="key">{`Key · ${set.key}`}</span> : null,
					elements.length ? <span key="elements">{formatCount(elements.length, 'element')}</span> : null,
					cards.length ? <span key="used">{`Used by ${formatCount(cards.length, 'card')}`}</span> : null,
				]} />
			</div>

			<section className="card-home__section" aria-label="Elements">
				<GroupHeader
					label="Elements"
					count={elements.length}
					countLabel="element"
					action={editLink(CARD_TAB.elements, 'Edit elements')}
				/>
				{panels.length ? panels.map(panel => (
					<div key={panel.key} className="card-home__group">
						{/* A set's elements land in one panel named for the set
						    unless an element names a group of its own; only a
						    named group is worth a label here. */}
						{panel.label ? <div className="micro-label card-home__group-label">{panel.label}</div> : null}
						<ul className="card-home__elements">
							{panel.entries.map(element => {
								const facts = elementFacts(element);
								return (
									<li key={element.name} className="card-home__element">
										<span className="card-home__element-label">
											{element?.label?.singular || element.name}
											<span className="card-def-element-row__name element-set-home__name">{element.name}</span>
										</span>
										<DataTypeBadge dataType={element?.dataType} size="sm" />
										{facts ? <span className="card-home__element-facts">{facts}</span> : null}
									</li>
								);
							})}
						</ul>
					</div>
				)) : (
					<p className="card-home__empty empty-line">
						No elements yet
						{editable ? <> · <Link to={`?tab=${CARD_TAB.elements}`} replace>Add one</Link></> : null}
					</p>
				)}
			</section>

			{cards.length ? (
				<section className="card-home__section" aria-label="Used by">
					<GroupHeader label="Used by" count={cards.length} countLabel="card" />
					<div className="card-home__badges">
						{cards.map(card => (
							<RecordBadge
								key={normalizeId(card?._id)}
								kind="card"
								record={card}
								title={card?.name || '(untitled card)'}
								size="sm"
								to={cardPagePath(card)}
							/>
						))}
					</div>
				</section>
			) : null}
		</div>
	);
}
